import { BsdsIcon } from '@bsds-lib-component/react';
import React, { useEffect, useMemo, useRef, useState } from 'react';

import type { TabItem, TabProps } from '../../types/componentType';

export type { TabItem, TabProps };

const Tab: React.FC<TabProps> = ({ tabs, defaultTabId, className = '', onActiveTabChange }) => {
  const visibleTabs = useMemo(
    () => tabs.filter((tab) => tab.visible !== false),
    [tabs],
  );

  const resolveInitialTabId = () => {
    if (defaultTabId && visibleTabs.some((tab) => tab.id === defaultTabId)) {
      return defaultTabId;
    }
    return visibleTabs[0]?.id || '';
  };

  const [activeTabId, setActiveTabId] = useState<string>(resolveInitialTabId);
  const [minTabWidths, setMinTabWidths] = useState<Map<string, number>>(new Map());
  const [tabWidths, setTabWidths] = useState<Map<string, number>>(new Map());
  const containerRef = useRef<HTMLDivElement>(null);
  const tabListRef = useRef<HTMLDivElement>(null);
  const tabButtonsRef = useRef<Map<string, HTMLButtonElement>>(new Map());
  const isDraggingRef = useRef<boolean>(false);
  const startXRef = useRef<number>(0);
  const scrollLeftRef = useRef<number>(0);
  const hasMovedRef = useRef<boolean>(false);

  const activeTab = visibleTabs.find((tab) => tab.id === activeTabId) || visibleTabs[0];
  const onActiveTabChangeRef = useRef(onActiveTabChange);
  onActiveTabChangeRef.current = onActiveTabChange;
  const visibleTabsRef = useRef(visibleTabs);
  visibleTabsRef.current = visibleTabs;

  useEffect(() => {
    if (!visibleTabs.some((tab) => tab.id === activeTabId)) {
      setActiveTabId(visibleTabs[0]?.id || '');
    }
  }, [activeTabId, visibleTabs]);

  useEffect(() => {
    if (!activeTabId) {
      return;
    }
    const tab = visibleTabsRef.current.find((item) => item.id === activeTabId);
    if (tab) {
      onActiveTabChangeRef.current?.(tab);
    }
  }, [activeTabId]);

  // Calculate minimum individual tab width based on content (icon + title)
  useEffect(() => {
    const calculateMinTabWidths = () => {
      if (tabButtonsRef.current.size === 0) return;

      const newMinTabWidths = new Map<string, number>();

      tabButtonsRef.current.forEach((button, tabId) => {
        if (button) {
          // Get computed styles for padding
          const computedStyle = window.getComputedStyle(button);
          const paddingLeft = parseFloat(computedStyle.paddingLeft) || 0;
          const paddingRight = parseFloat(computedStyle.paddingRight) || 0;

          let contentWidth = paddingLeft + paddingRight;

          // Measure icon container
          const iconDiv = button.firstElementChild as HTMLElement;
          if (iconDiv) {
            const iconRect = iconDiv.getBoundingClientRect();
            contentWidth += iconRect.width;
          }

          // Measure title span (second child)
          const titleSpan = button.children[1] as HTMLElement;
          if (titleSpan) {
            // Temporarily remove text constraints to measure natural width
            const originalWhiteSpace = titleSpan.style.whiteSpace;
            const originalMaxWidth = titleSpan.style.maxWidth;
            const originalOverflow = titleSpan.style.overflow;
            const originalTextOverflow = titleSpan.style.textOverflow;

            titleSpan.style.whiteSpace = 'nowrap';
            titleSpan.style.maxWidth = 'none';
            titleSpan.style.overflow = 'visible';
            titleSpan.style.textOverflow = 'clip';

            // Force reflow
            void titleSpan.offsetWidth;

            const titleWidth = titleSpan.scrollWidth;

            // Restore original styles
            titleSpan.style.whiteSpace = originalWhiteSpace;
            titleSpan.style.maxWidth = originalMaxWidth;
            titleSpan.style.overflow = originalOverflow;
            titleSpan.style.textOverflow = originalTextOverflow;

            // Add title width and its margin-left (8px)
            contentWidth += titleWidth + 8;
          }

          // Add some extra padding to ensure content doesn't feel cramped
          if (contentWidth > 0) {
            newMinTabWidths.set(tabId, Math.ceil(contentWidth + 8));
          }
        }
      });

      if (newMinTabWidths.size > 0) {
        setMinTabWidths(newMinTabWidths);
      }
    };

    // Use requestAnimationFrame to ensure DOM is fully rendered
    const timeoutId = setTimeout(() => {
      calculateMinTabWidths();
    }, 100);

    return () => clearTimeout(timeoutId);
  }, [visibleTabs]);

  // Calculate final tab widths: if there's extra space, distribute equally
  useEffect(() => {
    const calculateFinalTabWidths = () => {
      if (!containerRef.current || minTabWidths.size === 0) return;

      const containerWidth = containerRef.current.offsetWidth;

      // Calculate total minimum width needed
      let totalMinWidth = 0;
      minTabWidths.forEach((width) => {
        totalMinWidth += width;
      });

      const newTabWidths = new Map<string, number>();

      if (totalMinWidth <= containerWidth) {
        // There's extra space: distribute equally among all tabs
        const equalWidth = containerWidth / visibleTabs.length;
        visibleTabs.forEach((tab) => {
          newTabWidths.set(tab.id, equalWidth);
        });
      } else {
        // Not enough space: use minimum widths (will have scroll)
        minTabWidths.forEach((width, tabId) => {
          newTabWidths.set(tabId, width);
        });
      }

      setTabWidths(newTabWidths);
    };

    calculateFinalTabWidths();

    const resizeObserver = new ResizeObserver(() => {
      calculateFinalTabWidths();
    });

    if (containerRef.current) {
      resizeObserver.observe(containerRef.current);
    }

    window.addEventListener('resize', calculateFinalTabWidths);

    return () => {
      resizeObserver.disconnect();
      window.removeEventListener('resize', calculateFinalTabWidths);
    };
  }, [visibleTabs, minTabWidths]);

  // Handle mouse drag scrolling
  const handleMouseDown = (e: React.MouseEvent) => {
    if (!tabListRef.current) return;
    isDraggingRef.current = true;
    hasMovedRef.current = false;
    startXRef.current = e.pageX - tabListRef.current.offsetLeft;
    scrollLeftRef.current = tabListRef.current.scrollLeft;
    tabListRef.current.style.cursor = 'grabbing';
    tabListRef.current.style.userSelect = 'none';
  };

  const handleMouseLeave = () => {
    if (!tabListRef.current) return;
    isDraggingRef.current = false;
    hasMovedRef.current = false;
    tabListRef.current.style.cursor = 'grab';
    tabListRef.current.style.userSelect = '';
  };

  const handleMouseUp = () => {
    if (!tabListRef.current) return;
    isDraggingRef.current = false;
    tabListRef.current.style.cursor = 'grab';
    tabListRef.current.style.userSelect = '';
    // Reset after a short delay to allow click event
    setTimeout(() => {
      hasMovedRef.current = false;
    }, 100);
  };

  const handleMouseMove = (e: React.MouseEvent) => {
    if (!isDraggingRef.current || !tabListRef.current) return;
    e.preventDefault();
    const x = e.pageX - tabListRef.current.offsetLeft;
    const walk = (x - startXRef.current) * 2; // Scroll speed multiplier
    tabListRef.current.scrollLeft = scrollLeftRef.current - walk;
    hasMovedRef.current = true;
  };

  // Handle touch events for mobile - using addEventListener with passive: false
  useEffect(() => {
    const element = tabListRef.current;
    if (!element) return;

    const handleTouchStart = (e: TouchEvent) => {
      isDraggingRef.current = true;
      hasMovedRef.current = false;
      startXRef.current = e.touches[0].pageX - element.offsetLeft;
      scrollLeftRef.current = element.scrollLeft;
    };

    const handleTouchMove = (e: TouchEvent) => {
      if (!isDraggingRef.current) return;
      e.preventDefault();
      const x = e.touches[0].pageX - element.offsetLeft;
      const walk = (x - startXRef.current) * 2; // Scroll speed multiplier
      element.scrollLeft = scrollLeftRef.current - walk;
      hasMovedRef.current = true;
    };

    const handleTouchEnd = () => {
      isDraggingRef.current = false;
      // Reset after a short delay to allow click event
      setTimeout(() => {
        hasMovedRef.current = false;
      }, 100);
    };

    // Add event listeners with passive: false to allow preventDefault
    element.addEventListener('touchstart', handleTouchStart, { passive: true });
    element.addEventListener('touchmove', handleTouchMove, { passive: false });
    element.addEventListener('touchend', handleTouchEnd, { passive: true });

    return () => {
      element.removeEventListener('touchstart', handleTouchStart);
      element.removeEventListener('touchmove', handleTouchMove);
      element.removeEventListener('touchend', handleTouchEnd);
    };
  }, []);

  // Handle tab click - prevent if user was dragging
  const handleTabClick = (tabId: string, e: React.MouseEvent | React.TouchEvent) => {
    if (hasMovedRef.current) {
      e.preventDefault();
      e.stopPropagation();
      return;
    }
    setActiveTabId(tabId);
  };

  return (
    <div ref={containerRef} className={`bsds-width-100 bsds-pr-12 ${className}`}>
      {/* Tab Navigation */}
      <div
        ref={tabListRef}
        className="bsds-display-flex bsds-width-100 hide-scrollbar"
        style={{
          overflowX: 'auto',
          overflowY: 'hidden',
          WebkitOverflowScrolling: 'touch',
          cursor: 'grab',
          userSelect: 'none',
          touchAction: 'pan-x pinch-zoom',
        }}
        role="tablist"
        aria-label="Navegação por abas"
        onMouseDown={handleMouseDown}
        onMouseLeave={handleMouseLeave}
        onMouseUp={handleMouseUp}
        onMouseMove={handleMouseMove}
      >
        {visibleTabs.map((tab) => {
          const isActive = tab.id === activeTabId;
          const individualWidth = tabWidths.get(tab.id) || 80; // Fallback to 80px if not calculated yet
          return (
            <button
              key={tab.id}
              ref={(el) => {
                if (el) {
                  tabButtonsRef.current.set(tab.id, el);
                } else {
                  tabButtonsRef.current.delete(tab.id);
                }
              }}
              onClick={(e) => handleTabClick(tab.id, e)}
              className="bsds-display-flex bsds-flex-direction-column bsds-align-items-start bsds-justify-content-start bsds-p-8 bsds-ph-4 bsds-pb-12"
              style={{
                width: `${individualWidth}px`,
                minWidth: `${individualWidth}px`,
                border: 'none',
                background: 'transparent',
                cursor: 'pointer',
                position: 'relative',
                outline: 'none',
                touchAction: 'manipulation',
              }}
              onTouchEnd={(e) => {
                if (!hasMovedRef.current) {
                  handleTabClick(tab.id, e);
                }
              }}
              role="tab"
              aria-selected={isActive}
              aria-controls={`tabpanel-${tab.id}`}
              id={`tab-${tab.id}`}
            >
              {/* Icon */}
              <div
                className="bsds-display-flex bsds-align-items-center bsds-justify-content-center bsds-mb-4"
                style={{
                  fontSize: '20px',
                  color: isActive ? 'secondary-600' : 'rgba(0, 0, 0, 0.6)',
                }}
              >
                {tab.icon && typeof tab.icon === 'string' && (
                  <BsdsIcon icon={tab.icon} className="w-5 h-5" aria-hidden="true" />
                )}
                {tab.icon && typeof tab.icon !== 'string' && tab.icon}
              </div>
              {/* Title */}
              <span
                style={{
                  fontSize: '14px',
                  fontWeight: isActive ? 600 : 400,
                  color: isActive ? 'secondary-600' : 'rgba(0, 0, 0, 0.6)',
                  textAlign: 'center',
                  lineHeight: '1.2',
                  marginLeft: '8px',
                  marginBottom: '4px',
                  whiteSpace: 'nowrap',
                  overflow: 'hidden',
                  textOverflow: 'ellipsis',
                  maxWidth: '100%',
                }}
              >
                {tab.title}
              </span>
              {/* Active Indicator Bar */}
              {isActive && (
                <div
                  className="bg-secondary-600"
                  style={{
                    position: 'absolute',
                    bottom: 0,
                    left: 0,
                    right: 0,
                    height: '4px',
                    borderTopLeftRadius: '4px',
                    borderTopRightRadius: '4px',
                    zIndex: 10,
                  }}
                  aria-hidden="true"
                />
              )}
            </button>
          );
        })}
      </div>

      {/* Separator Line */}
      <div
        className="bsds-width-100 bg-gray-400"
        style={{
          height: '1.5px',
          marginTop: '-2px',
        }}
        aria-hidden="true"
      />

      {/* Tab Content */}
      <div
        role="tabpanel"
        id={`tabpanel-${activeTab?.id}`}
        aria-labelledby={`tab-${activeTab?.id}`}
        className="bsds-width-100"
      >
        {activeTab?.content}
      </div>
    </div>
  );
};

export default Tab;

